import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, User, LogOut, Shield, Trophy, ShoppingBag } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";

const NAV = [
    { to: "/", label: "Home", testid: "nav-home", priority: 1 },
    { to: "/roster", label: "Roster", testid: "nav-roster", priority: 1 },
    { to: "/stats", label: "Stats", testid: "nav-stats", priority: 1 },
    { to: "/standings", label: "Standings", testid: "nav-standings", priority: 1 },
    { to: "/schedule", label: "Upcoming Games", testid: "nav-schedule", priority: 1 },
    { to: "/news", label: "News", testid: "nav-news", priority: 1 },
    { to: "/tickets", label: "Tickets", testid: "nav-tickets", priority: 1 },
    { to: "/store", label: "Store", testid: "nav-store", priority: 1 },
    { to: "/gallery", label: "Gallery", testid: "nav-gallery", priority: 2 },
    { to: "/sponsors", label: "Sponsors", testid: "nav-sponsors", priority: 2 },
    { to: "/press", label: "Press", testid: "nav-press", priority: 2 },
    { to: "/about", label: "About", testid: "nav-about", priority: 2 },
    { to: "/staff", label: "Coaches", testid: "nav-staff", priority: 2 },
    { to: "/contact", label: "Contact", testid: "nav-contact", priority: 2 },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const { count: cartCount } = useCart();
    const navigate = useNavigate();

    return (
        <header
            data-testid="site-header"
            className="fixed top-0 inset-x-0 z-50 bg-bg/70 backdrop-blur-xl border-b border-line"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
                    <Logo size="sm" />
                    <span
                        data-testid="champions-badge"
                        title="2024–2025 ABA South Central Blue Division Wild Card Champions"
                        className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 border border-gold/50 bg-gold/10 text-gold text-[9px] tracking-[0.25em] uppercase font-black whitespace-nowrap"
                    >
                        <Trophy size={10} />
                        24–25 Champs
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                                `${n.priority === 2 ? "hidden xl:inline" : ""} text-[0.7rem] tracking-[0.18em] font-bold uppercase transition-colors ${
                                    isActive ? "text-gold" : "text-white hover:text-gold"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link
                        to="/store/checkout"
                        data-testid="header-cart"
                        title="Cart"
                        className="relative text-white hover:text-gold"
                    >
                        <ShoppingBag size={18} />
                        {cartCount > 0 && (
                            <span
                                data-testid="cart-count"
                                className="absolute -top-1.5 -right-2 bg-gold text-bg text-[9px] font-black rounded-full w-4 h-4 grid place-items-center"
                            >
                                {cartCount}
                            </span>
                        )}
                    </Link>
                    <Link
                        to="/tryouts"
                        data-testid="header-tryouts-btn"
                        className="btn-gold px-4 py-2 text-xs"
                    >
                        Tryouts
                    </Link>
                    {user ? (
                        <div className="flex items-center gap-2">
                            {user.role === "admin" && (
                                <Link
                                    to="/admin"
                                    data-testid="header-admin-link"
                                    className="text-gold text-xs tracking-widest uppercase font-bold flex items-center gap-1 hover:text-gold-bright"
                                >
                                    <Shield size={14} /> Admin
                                </Link>
                            )}
                            <Link to="/dashboard" data-testid="header-account-link" className="text-white hover:text-gold">
                                <User size={18} />
                            </Link>
                            <button data-testid="header-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="text-ink hover:text-white">
                                <LogOut size={18} />
                            </button>
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            data-testid="header-login-link"
                            className="text-xs tracking-[0.2em] uppercase font-bold text-white hover:text-gold whitespace-nowrap"
                        >
                            Sign In
                        </Link>
                    )}
                </div>

                <button
                    onClick={() => setOpen((o) => !o)}
                    className="lg:hidden text-white"
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-line bg-bg" data-testid="mobile-nav">
                    <div className="px-5 py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`m-${n.testid}`}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `text-sm tracking-[0.2em] uppercase font-bold ${
                                        isActive ? "text-gold" : "text-white"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/tryouts"
                            onClick={() => setOpen(false)}
                            data-testid="m-header-tryouts-btn"
                            className="btn-gold px-4 py-3 mt-2 text-xs text-center"
                        >
                            Tryouts
                        </Link>
                        {user ? (
                            <>
                                {user.role === "admin" && (
                                    <Link onClick={() => setOpen(false)} to="/admin" data-testid="m-header-admin-link" className="text-gold text-xs uppercase font-bold">Admin</Link>
                                )}
                                <button data-testid="m-header-logout-btn" onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-left text-ink text-xs uppercase font-bold">Sign Out</button>
                            </>
                        ) : (
                            <Link to="/login" onClick={() => setOpen(false)} data-testid="m-header-login-link" className="text-white text-xs uppercase font-bold">Sign In</Link>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
